import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import { useToastAlerts } from "../../../hooks/useReduxSelectors";

const CATEGORY_CONFIG = {
  success: { icon: CheckCircleIcon, color: "success.main" },
  info: { icon: InfoIcon, color: "info.main" },
  warning: { icon: WarningAmberIcon, color: "warning.main" },
  error: { icon: CancelIcon, color: "error.main" },
};

const ToastEventLog = ({ open, toastLogAnchorEl, onClose, dashboardCount }) => {
  const { t } = useTranslation("common");
  const toastEventArray = useToastAlerts();

  return (
    <Popover
      open={open}
      anchorEl={toastLogAnchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: dashboardCount > 1 ? "left" : "center" }}
      transformOrigin={{ vertical: "top", horizontal: dashboardCount > 1 ? "right" : "center" }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.dark",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            width: { xs: "calc(100vw - 16px)", sm: 360 },
            maxHeight: { xs: "70vh", sm: 420 },
            mt: 0.5,
          },
        },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t("Events")}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Event list */}
      <Box sx={{ overflowY: "auto", maxHeight: { xs: "calc(70vh - 48px)", sm: 370 } }}>
        {toastEventArray.length > 0 ? (
          toastEventArray.map((event, index) => {
            const cfg = CATEGORY_CONFIG[event.Category] || CATEGORY_CONFIG.info;
            const Icon = cfg.icon;
            return (
              <Stack
                key={index}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Icon sx={{ color: cfg.color, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem", lineHeight: 1.4 }}>
                    {event.Reason}
                  </Typography>
                  {event.timeStamp && (
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                      {event.timeStamp}
                    </Typography>
                  )}
                </Box>
              </Stack>
            );
          })
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("No events")}
            </Typography>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default ToastEventLog;